import type { StorefrontBootstrap } from "@emynor/storefront-sdk";
import { loc } from "./format";
import { hasLegalText, legalText } from "./legal";
import { translate, type TranslationKey } from "./translations";
import type { AppConfig, Branch, Lang, MenuItem } from "./types";

export const branchPage = /^\/branches\/([0-9a-f-]{36})(\/menu)?\/?$/i;

export function seoPage(input: {
  pathname: string;
  lang: Lang;
  branch: Branch | undefined;
  branches: Branch[] | undefined;
  product: MenuItem | undefined;
  storefront: StorefrontBootstrap;
  config: AppConfig;
  branchMenuHasItems: boolean;
}) {
  const { lang, branch, product, storefront, config, branchMenuHasItems } = input;
  const pathname = input.pathname.replace(/\/+$/, "") || "/";
  const brand = loc(config.title, lang);
  const configuredTitle = storefront.tenant.seo?.title?.trim();
  const configuredDescription = storefront.tenant.seo?.description?.trim();
  const heroDescription = loc(config.heroDescription, lang);
  const defaultDescription = (lang === storefront.tenant.defaultLocale && configuredDescription) || heroDescription || brand;
  const heroIds = storefront.storefront?.configuration["heroImageAssetIds"];
  const heroId = Array.isArray(heroIds) && typeof heroIds[0] === "string" ? heroIds[0] : null;
  const publishedHero = heroId
    ? storefront.storefront?.assetImages?.[heroId] ?? storefront.storefront?.assetUrls?.[heroId]
    : null;
  const defaultImage = storefront.tenant.seo?.socialImageUrl || publishedHero || storefront.tenant.logoImage || storefront.tenant.logoUrl || undefined;
  const published = storefront.mode === "published";
  const titleFor = (pageTitle: string) => `${brand} | ${pageTitle}`;
  const page = (titleKey: TranslationKey, descriptionKey: TranslationKey) => ({
    title: titleFor(translate(titleKey, lang)),
    description: translate(descriptionKey, lang),
    image: defaultImage,
    index: false,
    restaurant: undefined,
  });

  if (pathname === "/") return {
    title: titleFor((lang === storefront.tenant.defaultLocale && configuredTitle !== brand && configuredTitle) || translate("nav.home", lang)),
    description: defaultDescription,
    image: defaultImage,
    index: published,
    restaurant: undefined,
  };
  if (pathname === "/branches") return {
    title: titleFor(translate("branches.title", lang)),
    description: lang === "ar" ? `تعرّف على فروع ${brand} ومواعيد عملها.` : `Explore ${brand} branches and opening hours.`,
    image: defaultImage,
    index: published,
    restaurant: undefined,
  };
  const match = pathname.match(branchPage);
  if (match) {
    if (!branch) return page("branches.title", "branches.desc");
    const name = loc(branch.name, lang);
    const isMenu = Boolean(match[2]);
    return {
      title: titleFor(isMenu ? (lang === "ar" ? `قائمة ${name}` : `${name} menu`) : name),
      description: isMenu
        ? (lang === "ar" ? `تصفح قائمة ${name}.` : `Browse the menu for ${name}.`)
        : (lang === "ar" ? `تعرّف على فرع ${name}.` : `Explore the ${name} branch.`),
      image: branch.hasPhoto ? (branch.responsiveImage ?? branch.image) : defaultImage,
      imageAlt: name,
      index: published && (!isMenu || branchMenuHasItems),
      restaurant: isMenu ? undefined : branch,
    };
  }
  if (pathname.startsWith("/branchMenu/")) {
    const dineInBranch = input.branches?.find((item) => item.id === pathname.slice("/branchMenu/".length));
    if (!dineInBranch) return page("branchMenu.selectTitle", "branchMenu.selectDesc");
    const name = loc(dineInBranch.name, lang);
    return {
      title: titleFor(lang === "ar" ? `منيو ${name}` : `${name} dine-in menu`),
      description: lang === "ar" ? `تصفح منيو ${name} داخل المطعم.` : `Browse the dine-in menu for ${name}.`,
      image: dineInBranch.hasPhoto ? (dineInBranch.responsiveImage ?? dineInBranch.image) : defaultImage,
      index: false,
      restaurant: undefined,
    };
  }
  if (productSlugFromPath(pathname)) {
    if (!product) return page("menu.title", "menu.subtitle");
    const name = loc(product.name, lang);
    return {
      title: titleFor(name),
      description: summarize(loc(product.description, lang)) || (lang === "ar" ? `تعرف على ${name} من ${brand}.` : `Explore ${name} from ${brand}.`),
      image: product.responsiveImage ?? (product.image || defaultImage),
      imageAlt: name,
      index: published,
      restaurant: undefined,
    };
  }

  const legalPages = {
    "/privacy-policy": { key: "privacy", title: "footer.privacy" },
    "/terms-and-conditions": { key: "terms", title: "footer.terms" },
    "/return-policy": { key: "refund", title: "footer.returns" },
  } as const;
  const legal = legalPages[pathname as keyof typeof legalPages];
  if (legal) {
    const title = translate(legal.title, lang);
    const policySummary = hasLegalText(storefront, legal.key)
      ? summarize(legalText(storefront, legal.key, lang))
      : "";
    return {
      title: titleFor(title),
      description: policySummary.length >= 40
        ? policySummary
        : (lang === "ar" ? `اطّلع على ${title} لدى ${brand}.` : `Read ${brand}'s ${title.toLowerCase()}.`),
      image: defaultImage,
      index: published && hasLegalText(storefront, legal.key),
      restaurant: undefined,
    };
  }

  const pages: Record<string, [TranslationKey, TranslationKey]> = {
    "/main-menu": ["mainMenu.title", "mainMenu.subtitle"],
    "/branchMenu": ["branchMenu.selectTitle", "branchMenu.selectDesc"],
    "/menu": ["menu.title", "menu.subtitle"],
    "/order-type": ["orderType.title", "orderType.pageDesc"],
    "/cart": ["cart.title", "seo.cartDescription"],
    "/checkout": ["checkout.title", "checkout.subtitle"],
    "/login": ["login.title", "login.phoneStep"],
    "/account": ["account.title", "account.subtitle"],
    "/orders": ["orders.title", "orders.subtitle"],
    "/loyalty": ["loyalty.walletTitle", "loyalty.walletSubtitle"],
    "/order-success": ["success.title", "success.desc"],
    "/payment-return": ["seo.paymentStatusTitle", "seo.paymentStatusDescription"],
  };
  const copy = pages[pathname];
  if (copy) return page(copy[0], copy[1]);
  if (pathname.startsWith("/orders/")) return page("orders.view", "orders.subtitle");
  if (pathname.startsWith("/pay/")) return page("paymentLink.title", "seo.paymentLinkDescription");
  if (pathname.startsWith("/o/")) return page("seo.orderLinkTitle", "seo.orderLinkDescription");
  return page("notfound.title", "notfound.desc");
}

export function productSlugFromPath(pathname: string): string {
  const match = pathname.match(/^\/menu\/([^/]+)\/?$/);
  if (!match) return "";
  try { return decodeURIComponent(match[1]!); } catch { return ""; }
}

function summarize(text: string): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= 160) return normalized;
  return `${normalized.slice(0, 157).trimEnd()}…`;
}
